<template>
  <b-row align-h="center">
    <b-col cols="10" class="my-2" v-if="currentDirectory">
      <b-breadcrumb>
        <b-breadcrumb-item v-for="(item, $index) in parsedPath" :key="$index" :text="item.name" :active="item.active"
          @click="breadcrumbClicked(item)">
        </b-breadcrumb-item>
      </b-breadcrumb>
    </b-col>
    <b-col cols="2" class="mt-3">
      <b-spinner v-show="formLoading" label="Items loading"></b-spinner>
    </b-col>
    <!-- Col with folders -->
    <b-col cols="12" v-if="currentDirectory">
      <b-row>
        <b-col cols="12" class="mb-3">
          <h4>Folders</h4>
          <p v-show="!currentDirectory.directories || currentDirectory.directories.length === 0">No subdirectories</p>
          <MediaUploadPicker @create-folder-clicked="createFolderProxy" @create-file-clicked="createFileProxy"
            @create-video-clicked="createVideoProxy" @create-url-clicked="this.createURLProxy" />
        </b-col>
        <template v-if="view === 'grid'">
          <b-col cols="12" sm="6" md="4" lg="3" class="my-2" v-for="(folder, id) in currentDirectory.directories"
            :key="id">
            <MediaThumbnail v-if="folder.id !== hideFolder" :media="folder" :type="'folder'" :key="folder.id"
              :view="view" :showButtons="!isPicker" :isBusy="formLoading" @card-clicked="navigateTo(folder)"
              @move-clicked="moveFolderProxy(folder)" @delete-clicked="deleteFolderProxy(folder)"
              @rename-clicked="renameFolderProxy(folder, { ...folder, name: $event.newName })" />
          </b-col>
        </template>
        <template v-if="view === 'list'">
          <b-col cols="12" v-for="(folder, id) in currentDirectory.directories" :key="id">
            <MediaThumbnail v-if="folder.id !== hideFolder" :media="folder" :type="'folder'" :key="folder.id"
              :view="view" :showButtons="!isPicker" :isBusy="formLoading" @card-clicked="navigateTo(folder)"
              @move-clicked="moveFolderProxy(folder)" @delete-clicked="deleteFolderProxy(folder)"
              @rename-clicked="renameFolderProxy(folder, { ...folder, name: $event.newName })" />
          </b-col>
        </template>
      </b-row>
    </b-col>
    <!-- Col with files -->
    <b-col cols="12" v-if="currentDirectory && currentDirectory.files && !pickerFoldersOnly">
      <b-row>
        <b-col cols="12">
          <h4>Slides</h4>
          <p v-show="!currentDirectory.files || currentDirectory.files.length === 0">No files</p>
        </b-col>
        <template v-if="view === 'grid'">
          <b-col cols="12" sm="6" md="4" lg="3" class="my-2" v-for="(file, id) in currentDirectory.files" :key="id">
            <MediaThumbnail :media="file" :type="'file'" :key="file.id" :view="view" :showButtons="!isPicker"
              :isBusy="formLoading" :selected="checkFileSelected(file)" @card-clicked="fileClicked(file)"
              @move-clicked="moveFileProxy(file)" @delete-clicked="deleteFileProxy(file)"
              @change-clicked="changeFileProxy(currentDirectory, file)"
              @rename-clicked="renameFileProxy(file, { ...file, name: $event.newName })" />
          </b-col>
        </template>
        <template v-if="view === 'list'">
          <b-col cols="12" v-for="(file, id) in currentDirectory.files" :key="id">
            <MediaThumbnail :media="file" :type="'file'" :key="file.id" :view="view" :showButtons="!isPicker"
              :isBusy="formLoading" :selected="checkFileSelected(file)" @card-clicked="fileClicked(file)"
              @move-clicked="moveFileProxy(file)" @delete-clicked="deleteFileProxy(file)"
              @change-clicked="changeFileProxy(currentDirectory, file)"
              @rename-clicked="renameFileProxy(file, { ...file, name: $event.newName })" />
          </b-col>
        </template>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import mediaMixin from '@/views/admin/media/mediaMixin';
import MediaThumbnail from '@/components/MediaThumbnail.vue';
import MediaUploadPicker from '@/components/MediaUploadPicker.vue';
import formsMixin from '@/mixins/forms';

export default {
  mixins: [mediaMixin, formsMixin],
  components: { MediaThumbnail, MediaUploadPicker },
  props: {
    targetPath: {
      type: [String, Number],
      required: false,
    },
    isPicker: {
      type: [Boolean],
      required: false,
      default: false,
    },
    multiPick: {
      type: [Boolean],
      required: false,
      default: false,
    },
    pickerFoldersOnly: {
      type: [Boolean],
      required: false,
      default: false,
    },
    pickedFilesArray: {
      type: [Array],
      required: false,
    },
    view: {
      type: [String],
      required: false,
      default: 'grid',
    },
    hideFolder: {
      type: [Number],
      required: false,
      default: -1,
    },
    orientation: {
      type: [Number],
      required: false,
    },
  },
  watch: {
    targetPath: {
      immediate: true,
      handler(newPath) {
        if (newPath) {
          this.currentPath = String(this.targetPath).split('/');
        } else {
          this.currentPath = [];
        }
        // update
        // add a default media library path
        const finalArray = [
          {
            id: 'root',
            name: 'Media Library',
            active: false,
          },
        ];

        // parse the paths
        if (this.currentPath && this.currentPath.length > 0) {
          this.currentPath.forEach((fid, idx) => {
            // check if the current path contains this id already so we can skip it
            const exists = this.parsedPath.find(e => String(e.id) === String(fid));
            if (exists) {
              finalArray.push(exists);
            } else {
              // put the new item in the array
              const nextItem = {
                id: fid,
                name: '...',
                active: true,
              };
              finalArray.push(nextItem);
              // if it's the last item skip async check since we're already pulling in the folder
              // async call
              if (idx < this.currentPath.length - 1) {
                this.getFolder({ id: fid }).then((data) => {
                  nextItem.name = data.name;
                }).catch(() => {this.navigateToUnsafe({id:'root'})});
              }
            }
          });
        }

        this.parsedPath = finalArray;

        // show the current one
        this.showDirectory({ id: [...this.currentPath].pop() }, this.orientation);
      },
    },
  },
  data() {
    return {
      currentDirectory: null,
      currentPath: [],
      parsedPath: [],
    };
  },
  methods: {
    breadcrumbClicked(item) {
      if (!item.active && !this.formLoading) {
        if (item.id) {
          this.navigateTo({ id: item.id });
        } else {
          this.$emit('route-update', { name: 'media', params: { path: '' } });
        }
      }
    },
    showDirectory(dirObj, orientation) {
      this.getFolder(dirObj, orientation).then((data) => {
        // add rename flag to subdirs
        data.directories.forEach((e) => { e.rename = false; e.originalname = e.name; });
        this.currentDirectory = data;
        this.$emit('current-folder-update', { id: this.currentDirectory.id });

        const tryFind = this.parsedPath.find(e => String(e.id) === String(data.id));
        if (!tryFind && this.currentDirectory.directory_id !== null) {
          this.parsedPath.push({ name: this.currentDirectory.name, active: true, id: this.currentDirectory.id });
        } else {
          this.parsedPath[this.parsedPath.indexOf(tryFind)] = { name: this.currentDirectory.name, active: true, id: this.currentDirectory.id };
        }
        // make the last one active only
        if (this.parsedPath.length > 0) {
          this.parsedPath.forEach((element, index) => {
            // eslint-disable-next-line no-param-reassign
            element.active = false;
            if (index === this.parsedPath.length - 1) {
              // eslint-disable-next-line no-param-reassign
              element.active = true;
            }
          });
        }
      }).catch(() => {this.navigateToUnsafe({id:'root'})});
    },
    navigateToUnsafe(dir) {
      // check if navigating to the root
      if (dir.id === 'root') {
        this.currentPath = [];
      } else {
        // check if navigating backwards
        const foundInPath = this.currentPath.find(e => String(e) === String(dir.id));
        if (foundInPath) {
          const removeFrom = this.currentPath.indexOf(foundInPath) + 1;
          const removeLength = this.currentPath.length - removeFrom;

          this.currentPath.splice(removeFrom, removeLength);
        } else {
          this.currentPath.push(dir.id);
        }
      }

      const newPath = this.currentPath.join('/');
      this.$emit('route-update', { name: 'media', params: { path: newPath || null } });
    },
    navigateTo(dir) {
      if (this.formLoading) return;
      this.navigateToUnsafe(dir);
    },
    getThumbnail(file) {
      if (file.thumb_url && file.thumb_url.length) {
        return file.thumb_url;
      }
      if (file.mime_type.toLowerCase().indexOf('image') >= 0) {
        return (`/storage/${file.path}/${file.real_name}`);
      }

      return '';
    },
    deleteFolderProxy(folder) {
      const idxToUpdate = this.currentDirectory.directories.indexOf(folder);
      this.deleteFolder(folder).then(() => {
        this.currentDirectory.directories.splice(idxToUpdate, 1);
        // if no files or folders left nuke the folder as well and go to root
        if (this.currentDirectory.files.length === 0 && this.currentDirectory.directories.length === 0) {
          this.navigateToUnsafe({id:'root'})
        }
      }).catch(() => { });
    },
    createFolderProxy() {
      this.$root.$emit('modal-show-create-folder', {
        title: 'Create a New Folder',
        body: 'Enter a new folder name:',
        cbConfirm: (inputValue) => {
          if (!inputValue || inputValue.length < 3) {
            this.$root.$emit('notification-add', { content: 'Invalid folder name.', variant: 'danger' });
          } else {
            this.createFolder({ name: inputValue, directory_id: this.currentDirectory.id })
              .then((data) => { this.currentDirectory.directories.push(data); });
          }
        },
        confirmText: 'Create',
        cancelText: 'Cancel',
        inputPlaceholder: 'Folder name',
      });
    },
    renameFolderProxy(folder, folderUpdate) {
      // eslint-disable-next-line no-unreachable
      if (!folderUpdate.name || folderUpdate.name.length < 3) {
        this.$root.$emit('notification-add', { content: 'Invalid folder name.', variant: 'danger' });
      } else {
        const idxToUpdate = this.currentDirectory.directories.indexOf(folder);
        this.updateFolder(folderUpdate).then((updatedData) => {
          this.currentDirectory.directories[idxToUpdate] = updatedData;
        })
          .catch(() => { })
          .finally(() => { });
      }
    },
    createFileProxy() {
      // save current dir reference for background upload as user might navigate away
      const currentDirRef = this.currentDirectory;
      this.$root.$emit('modal-show-upload-files', {
        title: 'Upload files',
        body: 'Select the files to upload',
        cbConfirm: (incomingFiles) => {
          const filesPromises = [];
          incomingFiles.forEach((file) => {
            const filePromise = this.createFile({ name: file.name.split('.').slice(0, -1).join('.') }, currentDirRef, file);
            filesPromises.push(filePromise);
            this.$root.$emit('notification-add', {
              content: `File ${file.name} uploading...`,
              variant: 'info',
              ttl: Infinity,
              promise: filePromise,
            });
            // handle the promise
            filePromise.then((fileInfo) => {
              if (currentDirRef.id === this.currentDirectory.id) { currentDirRef.files.push(fileInfo); }
            }).catch(() => { });
          });
        },
        confirmText: 'Upload',
        cancelText: 'Cancel',
        isMultiple: true,
      });
    },
    createVideoProxy() {
      // save current dir reference for background upload as user might navigate away
      const currentDirRef = this.currentDirectory;
      this.$root.$emit('modal-show-upload-videos', {
        title: 'Upload videos',
        body: 'Select the files to upload',
        cbConfirm: (incomingFiles) => {
          const filesPromises = [];
          incomingFiles.forEach((file) => {
            const filePromise = this.createFile({ name: file.name.split('.').slice(0, -1).join('.') }, currentDirRef, file, 'video');
            filesPromises.push(filePromise);
            this.$root.$emit('notification-add', {
              content: `File ${file.name} uploading...`,
              variant: 'info',
              ttl: Infinity,
              promise: filePromise,
            });
            // handle the promise
            filePromise.then((fileInfo) => {
              if (currentDirRef.id === this.currentDirectory.id) { currentDirRef.files.push(fileInfo); }
            }).catch(() => { });
          });
        },
        confirmText: 'Upload',
        cancelText: 'Cancel',
        isMultiple: true,
      });
    },
    deleteFileProxy(file) {
      const idxToUpdate = this.currentDirectory.files.indexOf(file);
      this.deleteFile(file).then(() => {
        this.currentDirectory.files.splice(idxToUpdate, 1);
        // if no files or folders left nuke the folder as well and go to root
        if (this.currentDirectory.files.length === 0 && this.currentDirectory.directories.length === 0) {
          this.navigateToUnsafe({id:'root'})
        }
      }).catch(() => { });
    },
    renameFileProxy(file, fileUpdate) {
      // eslint-disable-next-line no-unreachable
      if (!fileUpdate.name || fileUpdate.name.length < 3) {
        this.$root.$emit('notification-add', { content: 'Invalid file name.', variant: 'danger' });
      } else {
        const idxToUpdate = this.currentDirectory.files.indexOf(file);
        this.updateFile(fileUpdate).then((updatedData) => {
          this.currentDirectory.files[idxToUpdate] = updatedData;
        })
          .catch(() => { })
          .finally(() => { });
      }
    },
    moveFileProxy(fileObj) {
      const currentDirRef = this.currentDirectory;
      this.$root.$emit('modal-show-file-picker', {
        title: 'Move to',
        body: '',
        cbConfirm: (moveToFolderId) => {
          if (moveToFolderId !== currentDirRef.id) {
            this.updateFile({ ...fileObj, directory_id: moveToFolderId })
              .then((newFile) => {
                if (this.currentDirectory.id !== newFile.directory_id) { this.currentDirectory.files = this.currentDirectory.files.filter(e => e !== fileObj); } else { this.currentDirectory.files[this.currentDirectory.files.indexOf(fileObj)] = newFile; }
              })
              .catch(() => { })
              .finally(() => { });
          }
        },
        confirmText: 'Move',
        cancelText: 'Cancel',
        multiPick: false,
        pickerFolders: true,
      });
    },
    moveFolderProxy(folderObj) {
      const currentDirRef = this.currentDirectory;
      this.$root.$emit('modal-show-file-picker', {
        title: 'Move to',
        body: '',
        cbConfirm: (moveToFolderId) => {
          if (moveToFolderId !== currentDirRef.id && moveToFolderId !== folderObj.id) {
            this.updateFolder({ ...folderObj, directory_id: moveToFolderId })
              .then((newFolder) => {
                if (this.currentDirectory.id !== newFolder.directory_id) { this.currentDirectory.directories = this.currentDirectory.directories.filter(e => e !== folderObj); } else { this.currentDirectory.directories[this.currentDirectory.directories.indexOf(folderObj)] = newFolder; }
              })
              .catch(() => { })
              .finally(() => { });
          }
        },
        confirmText: 'Move',
        cancelText: 'Cancel',
        multiPick: false,
        pickerFolders: true,
        folderIgnoreId: folderObj.id,
      });
    },
    changeFileProxy(currentDir, fileObj) {
      this.$root.$emit('modal-show-change-file', {
        file: fileObj,
        dir: currentDir,
      });
    },
    fileClicked(file) {
      this.$emit('file-clicked', file);
      /* if (file.type === 'url') {
        window.open(file.path, '_blank');
        return;
      } */
      if (!this.isPicker) {
        if (file.type === 'url' || file.mime_type.indexOf('image') === 0 || file.mime_type.indexOf('video') === 0) {
          this.$root.$emit('modal-show-file-preview', {
            title: `Slide preview: ${file.name}`,
            body: '',
            file,
          });
        } else {
          window.open(file.file_url, '_blank');
        }
      }
    },
    checkFileSelected(file) {
      if (this.isPicker && this.pickedFilesArray) {
        return !!this.pickedFilesArray.find(f => f.id === file.id);
      }
      return false;
    },
  },
};
</script>
